import ApiSistema from '@/core/apis/ApiSistema';
import { IInspecao, IInspecaoAcesso } from '@/models/Entidades/IInspecao';
import { IResposta } from '@/core/models/IResposta';
import { IServicoBase } from '@/core/models/IServicoBase';
import { IRepostaConsulta } from '@/core/models/IRepostaConsulta';
import { IInspecaoParametrosConsulta } from '@/models/ParametrosRequisicao/Consulta/IInspecaoParametrosConsulta';
import { IDTOInspecaoResumo } from '@/models/DTO/IDTOInspecaoResumo';
import { IParametrosBuscaRapida } from '@/core/models/Consulta/IParametrosBuscaRapida';
import { IItemBuscaRapida } from '@/core/models/Consulta/IItemBuscaRapida';
import { IRetornoRelatorio } from '@/core/models/IRetornoRelatorio';
import { IDTORelatorioFotografico } from '@/models/DTO/IDTORelatorioFotografico';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { IRetornoEmail } from '@/core/models/IRetornoEmail';
import { IDTOEnviarEmail } from '@/models/DTO/IDTOEnviarEmail';
import { IInspecaoValores } from '@/models/Entidades/IInspecaoValores';
/**
 * Serviço de Inspeção
 */

class ServicoInspecao implements IServicoBase<IInspecao> {
  endPoint = 'inspecoes';

  apiSistema = new ApiSistema();

  public async incluir(objeto: IInspecao): Promise<IResposta> {
    const result: any = await this.apiSistema.post(`${this.endPoint}`, objeto);
    return result.data;
  }

  public async gerarPdf(codigo:number): Promise<IRetornoRelatorio> {
    const result: any = await this.apiSistema.post(`${this.endPoint}/${codigo}/imprimir`);
    return result.data;
  }

  public async revisar(codigo:number): Promise<IResposta> {
    const result: any = await this.apiSistema.post(`${this.endPoint}/${codigo}/revisao`);
    return result.data;
  }

  public async enviarEmail(objeto:IDTOEnviarEmail): Promise<IRetornoEmail> {
    const result: any = await this.apiSistema.post(`${this.endPoint}/email/enviar`, objeto);
    return result.data;
  }

  public async montarEmail(objeto:IInspecao): Promise<string> {
    const result: any = await this.apiSistema.post(`${this.endPoint}/email/montar`, objeto);
    return result.data;
  }

  public async atualizar(objeto: IInspecao, chave?:string): Promise<IResposta> {
    let urlChave = '';
    if (UtilitarioGeral.valorValido(chave)) {
      urlChave = `?chave=${chave}`;
    }
    const result: any = await this.apiSistema.put(`${this.endPoint}/${objeto.codigo}${urlChave}`, objeto);
    return result.data;
  }

  public async obter(codigo: number): Promise<IInspecao> {
    const result: any = await this.apiSistema.get(`${this.endPoint}/${codigo}`);
    return result.data;
  }

  public async obterInspecaoChave(chave: string): Promise<IInspecaoAcesso> {
    const result: any = await this.apiSistema.get(`${this.endPoint}/acesso/${chave}`);
    return result.data;
  }

  public async obterInspecoes(parametrosConsulta: IInspecaoParametrosConsulta): Promise<IRepostaConsulta<IDTOInspecaoResumo>> {
    let parametrosAdicionais = '';

    if (parametrosConsulta.valorPesquisa !== undefined) {
      parametrosAdicionais += `?ValorPesquisa=${parametrosConsulta.valorPesquisa}`;
    }

    if (parametrosConsulta.status !== undefined) {
      parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
      parametrosAdicionais += `Status=${parametrosConsulta.status}`;
    }

    if (parametrosConsulta.numeroPagina !== undefined && parametrosConsulta.numeroPagina > 0) {
      parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
      parametrosAdicionais += `NumeroPagina=${parametrosConsulta.numeroPagina}`;
    }
    if (parametrosConsulta.qtdeRegistrosPagina !== undefined && parametrosConsulta.qtdeRegistrosPagina > 0) {
      parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
      parametrosAdicionais += `QtdeRegistrosPagina=${parametrosConsulta.qtdeRegistrosPagina}`;
    }
    if (parametrosConsulta.qtdeRegistrosTotal !== undefined && parametrosConsulta.qtdeRegistrosTotal > 0) {
      parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
      parametrosAdicionais += `QtdeRegistrosTotal=${parametrosConsulta.qtdeRegistrosTotal}`;
    }
    if (parametrosConsulta.ordenacao !== undefined) {
      parametrosConsulta.ordenacao.forEach((item) => {
        parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
        parametrosAdicionais += `Ordenacao=${item}`;
      });
    }
    if (parametrosConsulta.codigos !== undefined) {
      parametrosConsulta.codigos.forEach((codigo) => {
        parametrosAdicionais += (parametrosAdicionais !== '' ? '&' : '?');
        parametrosAdicionais += `Codigos=${codigo}`;
      });
    }
    const result: any = await this.apiSistema.get(`${this.endPoint}${parametrosAdicionais}`);
    return result.data;
  }

  public async buscaRapida(parametrosConsultaRapida: IParametrosBuscaRapida): Promise<IItemBuscaRapida[]> {
    const parametros = this.apiSistema.obterParametrosBuscaRapida(parametrosConsultaRapida);
    const result: any = await this.apiSistema.get(`${this.endPoint}/busca-rapida${parametros}`);
    return result.data;
  }

  public async estruturaRelatorioFotografico(): Promise<IDTORelatorioFotografico[]> {
    const result: any = await this.apiSistema.get(`${this.endPoint}/estrutura/relatorio/fotografico`);
    return result.data;
  }

  public async deletar(codigo: number): Promise<IResposta> {
    const result: any = await this.apiSistema.delete(`${this.endPoint}/${codigo}`);
    return result.data;
  }

  public async incluirInspecaoValores(objeto: IInspecaoValores): Promise<IResposta> {
    const result: any = await this.apiSistema.post(`${this.endPoint}/valores`, objeto);
    return result.data;
  }

  public async gerarPdfInspecaoValores(codigo:number): Promise<IRetornoRelatorio> {
    const result: any = await this.apiSistema.post(`${this.endPoint}/valores/${codigo}/imprimir`);
    return result.data;
  }

  public async atualizarInspecaoValores(objeto: IInspecaoValores): Promise<IResposta> {
    const result: any = await this.apiSistema.put(`${this.endPoint}/valores/${objeto.codigo}`, objeto);
    return result.data;
  }

  public async obterInspecaoValores(codigo: number): Promise<IInspecaoValores> {
    const result: any = await this.apiSistema.get(`${this.endPoint}/valores/${codigo}`);
    return result.data;
  }

  public async obterInspecaoValoresPorInspecao(codigoInspecao: number): Promise<IInspecaoValores> {
    const result: any = await this.apiSistema.get(`${this.endPoint}/${codigoInspecao}/valores`);
    return result.data;
  }
}

export default ServicoInspecao;
